
import { useState } from 'react';
import { PrayerRequest, PrayerStatus } from '@/types';
import { formatFullName } from '@/lib/format';
import { Table, Column } from '@/components/ui/Table';
import { Badge, getStatusBadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui';
import { Input } from '@/components/ui/Input';
import { Select, SelectOption } from '@/components/ui/Select';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { formatDate, truncate } from '@/lib/utils';
import { ArchiveFilters } from '@/hooks/useArchive';

export interface ArchiveTableProps {
  prayers: PrayerRequest[];
  isLoading?: boolean;
  filters: ArchiveFilters;
  onFilterChange: (filters: ArchiveFilters) => void;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onArchive: (id: string) => Promise<boolean>;
  onBulkArchive: (olderThanDays: number) => Promise<{ archivedCount: number } | null>;
  onExport: () => Promise<Blob | null>;
}

const statusOptions: SelectOption[] = [
  { value: '', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'displayed', label: 'Displayed' },
  { value: 'answered', label: 'Answered' },
  { value: 'archived', label: 'Dismissed' },
  { value: 'dismissed', label: 'Dismissed' },
];

export function ArchiveTable({
  prayers,
  isLoading = false,
  filters,
  onFilterChange,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onArchive,
  onBulkArchive,
  onExport,
}: ArchiveTableProps) {
  const [selectedPrayers, setSelectedPrayers] = useState<Set<string>>(new Set());
  const [showBulkArchiveDialog, setShowBulkArchiveDialog] = useState(false);
  const [bulkArchiveDays, setBulkArchiveDays] = useState(30);
  const [isBulkArchiving, setIsBulkArchiving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const handleBulkArchive = async () => {
    setIsBulkArchiving(true);
    const result = await onBulkArchive(bulkArchiveDays);
    setIsBulkArchiving(false);
    if (result) {
      setShowBulkArchiveDialog(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    const blob = await onExport();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `prayers-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    setIsExporting(false);
  };

  const columns: Column<PrayerRequest>[] = [
    {
      key: 'body',
      header: 'Prayer',
      render: (prayer) => (
        <div className="max-w-md">
          <p className="text-slate-900 dark:text-white">
            {truncate(prayer.body, 100)}
          </p>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {formatFullName(prayer.firstName, prayer.lastName) || 'Anonymous'} • {prayer.location || 'No location'}
          </p>
        </div>
      ),
    },
    {
      key: 'type',
      header: 'Type',
      sortable: true,
      render: (prayer) => (
        <Badge variant={prayer.type === 'prayer' ? 'info' : 'success'}>
          {prayer.type === 'prayer' ? 'Prayer' : 'Answered Prayer'}
        </Badge>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      render: (prayer) => (
        <span className="text-slate-600 dark:text-slate-400 capitalize">
          {prayer.category || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      render: (prayer) => (
        <Badge variant={getStatusBadgeVariant(prayer.status)}>
          {prayer.status.charAt(0).toUpperCase() + prayer.status.slice(1)}
        </Badge>
      ),
    },
    {
      key: 'createdAt',
      header: 'Submitted',
      sortable: true,
      render: (prayer) => (
        <span className="text-slate-600 dark:text-slate-400">
          {formatDate(prayer.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      width: '100px',
      render: (prayer) => (
        <div className="flex items-center gap-2">
          {prayer.status !== PrayerStatus.ARCHIVED && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onArchive(prayer.id)}
              title="Archive"
            >
              <ArchiveIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              label="Search"
              placeholder="Search prayers..."
              value={filters.search || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, search: e.target.value })
              }
            />
          </div>
          <div className="w-48">
            <Select
              label="Status"
              options={statusOptions}
              value={filters.status || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, status: e.target.value || undefined })
              }
            />
          </div>
          <div className="w-40">
            <Input
              label="Start Date"
              type="date"
              value={filters.startDate || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, startDate: e.target.value || undefined })
              }
            />
          </div>
          <div className="w-40">
            <Input
              label="End Date"
              type="date"
              value={filters.endDate || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, endDate: e.target.value || undefined })
              }
            />
          </div>
          <Button
            variant="ghost"
            onClick={() => onFilterChange({})}
          >
            Clear Filters
          </Button>
        </div>
      </Card>

      {/* Actions Bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => setShowBulkArchiveDialog(true)}
            leftIcon={<ArchiveIcon className="w-4 h-4" />}
          >
            Bulk Archive
          </Button>
          <Button
            variant="secondary"
            onClick={handleExport}
            isLoading={isExporting}
            leftIcon={<ExportIcon className="w-4 h-4" />}
          >
            Export CSV
          </Button>
        </div>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {totalItems} total prayers
        </p>
      </div>

      {/* Table */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <Table
          columns={columns}
          data={prayers}
          keyExtractor={(prayer) => prayer.id}
          isLoading={isLoading}
          emptyMessage="No prayers found"
          selectable
          selectedKeys={selectedPrayers}
          onSelectionChange={setSelectedPrayers}
          page={page}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
        />
      </div>

      {/* Bulk Archive Dialog */}
      <ConfirmDialog
        isOpen={showBulkArchiveDialog}
        onClose={() => setShowBulkArchiveDialog(false)}
        onConfirm={handleBulkArchive}
        title="Bulk Archive Prayers"
        message={
          <div className="space-y-4">
            <p>
              This will archive all prayers older than the specified number of
              days that have already been displayed or answered.
            </p>
            <Input
              label="Archive prayers older than (days)"
              type="number"
              min={1}
              value={bulkArchiveDays}
              onChange={(e) => setBulkArchiveDays(Number(e.target.value))}
            />
          </div>
        }
        confirmLabel="Archive"
        variant="default"
        isLoading={isBulkArchiving}
      />
    </div>
  );
}

// Icons
function ArchiveIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"
      />
    </svg>
  );
}

function ExportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
      />
    </svg>
  );
}
